import { useContext, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { ZoomContext } from "../../zoom/zoom-context";
import SpeakingIndicator from "./SpeakingIndicator";

export type StageLayout = "grid" | "two" | "sidebar";

interface Props {
  participants: any[];
  selfId?: number;
  /** userIds currently talking. Tiles in this set get a highlight ring and
   *  equaliser bars; several may be active at once. */
  speakingIds?: ReadonlySet<number>;
  /**
   * - "grid"    — equal grid (default for 1 or 3+ participants)
   * - "two"     — one big tile of the other person, small PiP of self
   * - "sidebar" — narrow vertical strip used while a screen share is on
   */
  layout: StageLayout;
}

interface TileBoxStyle {
  /** sx merged onto the tile wrapper. */
  box?: any;
  /** Whether this tile is the self-PiP overlay in "two" layout. */
  isSelfPip?: boolean;
}

/**
 * Renders one tile per participant using the Zoom Video SDK's
 * `mediaStream.renderVideo` API onto a per-tile <canvas> (or <video> for
 * the local user when `isRenderSelfViewWithVideoElement()` returns true).
 * This matches the rendering mode used by the legacy /src/zoom flow and
 * sidesteps the cross-origin-isolation requirements of `attachVideo` /
 * `<video-player>` web components.
 */
export default function VideoStage({
  participants,
  selfId,
  speakingIds,
  layout,
}: Readonly<Props>) {
  const zmClient = useContext(ZoomContext);
  // Per-userId canvas (or video) element refs.
  const elementsRef = useRef<Map<number, HTMLCanvasElement | HTMLVideoElement>>(
    new Map()
  );
  // Track which userIds have a live render so we don't double-render.
  const renderedRef = useRef<Set<number>>(new Set());
  // Stable per-userId ref-setters so React doesn't fire null/set ping-pong.
  const settersRef = useRef<
    Map<number, (el: HTMLCanvasElement | HTMLVideoElement | null) => void>
  >(new Map());

  const getRefSetter = (userId: number) => {
    let setter = settersRef.current.get(userId);
    if (!setter) {
      setter = (el) => {
        if (el) elementsRef.current.set(userId, el);
        else elementsRef.current.delete(userId);
      };
      settersRef.current.set(userId, setter);
    }
    return setter;
  };

  const ordered = orderForLayout(participants, selfId, layout);

  // Drop ref-setter cache for users no longer in the session.
  useEffect(() => {
    const liveIds = new Set(participants.map((p) => p.userId));
    for (const id of Array.from(settersRef.current.keys())) {
      if (!liveIds.has(id)) settersRef.current.delete(id);
    }
  }, [participants]);

  // On VideoStage unmount, detach SDK render from any canvas we'd
  // attached. Without this, when the guest's lobby VideoStage unmounts
  // on admit, the SDK still considers those (now-detached) canvases as
  // active render targets — and the freshly-mounted active-stage
  // VideoStage's renderVideo races against the stale attachment, which
  // is why "video on in lobby" wasn't surviving the transition until a
  // manual toggle.
  useEffect(() => {
    return () => {
      const stream: any = zmClient?.getMediaStream?.();
      if (!stream) return;
      for (const userId of Array.from(renderedRef.current)) {
        const el = elementsRef.current.get(userId);
        if (!el) continue;
        try {
          stream.stopRenderVideo(el, userId);
        } catch {
          /* ignore */
        }
      }
      renderedRef.current.clear();
    };
  }, []);

  // Start / stop video renders as bVideoOn flips for any user. Self ALSO
  // gets a parallel video-capturing-change handler below; both paths
  // dedupe through `renderedRef` so they cooperate without re-attaching
  // twice. Why both? On first join the SDK fires "Started" before the
  // self Tile has mounted, so the el isn't in elementsRef yet and the
  // event handler no-ops — this participants-driven loop is what
  // actually paints the local user the first time. On subsequent
  // toggles the event handler wins (it runs in lockstep with stop/start
  // Video) and this loop's renderedRef check skips the duplicate.
  useEffect(() => {
    const stream: any = zmClient?.getMediaStream?.();
    if (!stream) return;
    let cancelled = false;

    const tryRender = async (
      userId: number,
      el: HTMLCanvasElement | HTMLVideoElement,
      retries = 5
    ) => {
      // Pixel dimensions for the SDK to encode. The CSS sizes will scale
      // it; using something around 360p is plenty for telehealth.
      const w = 640;
      const h = 360;
      for (let i = 0; i < retries; i++) {
        try {
          // Only size the canvas on first render. Re-assigning width on
          // a canvas that was previously rendered to clears its backing
          // buffer (HTML spec) and corrupts the SDK's attached-renderer
          // state on the next renderVideo, which is what was producing
          // the persistent black canvas after a stopVideo/startVideo
          // cycle for ANY user (self or peer).
          if (el instanceof HTMLCanvasElement && el.width !== w) {
            el.width = w;
            el.height = h;
          }
          await stream.renderVideo(el, userId, w, h, 0, 0, 2 /* 360p */);
          renderedRef.current.add(userId);
          return;
        } catch (e) {
          // The SDK throws if the user's video stream isn't fully published
          // yet — common on the guest side right after admit because the
          // host's stream was already going before we mounted. Back off
          // and retry instead of giving up.
          if (i === retries - 1) {
            console.warn("renderVideo failed", userId, e);
            return;
          }
          await new Promise((r) => setTimeout(r, 400));
          if (cancelled) return;
        }
      }
    };

    (async () => {
      // Start renders for users newly broadcasting.
      for (const p of participants) {
        if (cancelled) return;
        if (!p.bVideoOn) continue;
        if (renderedRef.current.has(p.userId)) continue;
        const el = elementsRef.current.get(p.userId);
        if (!el) continue;
        await tryRender(p.userId, el);
      }
      // Stop renders for users no longer broadcasting (or who left).
      const onIds = new Set(
        participants.filter((p) => p.bVideoOn).map((p) => p.userId)
      );
      for (const userId of Array.from(renderedRef.current)) {
        if (!onIds.has(userId)) {
          const el = elementsRef.current.get(userId);
          try {
            if (el) await stream.stopRenderVideo(el, userId);
          } catch {
            /* ignore */
          }
          renderedRef.current.delete(userId);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [participants, zmClient, selfId]);

  // Self render is event-driven so it stays in lockstep with the SDK's
  // own start/stop cycle. Calling renderVideo on the same canvas after a
  // stopVideo/startVideo round-trip via the participants effect produced
  // a black canvas — the SDK's internal "attached" state didn't reset.
  // `video-capturing-change` is the legacy-blessed signal for self.
  useEffect(() => {
    const stream: any = zmClient?.getMediaStream?.();
    if (!stream || !zmClient || selfId === undefined) return;

    const renderSelf = () => {
      const el = elementsRef.current.get(selfId);
      if (!el) return;
      if (renderedRef.current.has(selfId)) return;
      const w = 640;
      const h = 360;
      try {
        if (el instanceof HTMLCanvasElement && el.width !== w) {
          el.width = w;
          el.height = h;
        }
        stream.renderVideo(el, selfId, w, h, 0, 0, 2 /* 360p */);
        renderedRef.current.add(selfId);
      } catch (e) {
        console.warn("self renderVideo failed", e);
      }
    };
    const stopSelf = () => {
      const el = elementsRef.current.get(selfId);
      try {
        if (el) stream.stopRenderVideo(el, selfId);
      } catch {
        /* ignore */
      }
      renderedRef.current.delete(selfId);
    };

    // Catch-up on mount: if the SDK is already capturing (e.g. we
    // remounted after a phase change while video was on), render now —
    // we missed the original "Started" event.
    try {
      if (stream.isCapturingVideo?.()) renderSelf();
    } catch {
      /* ignore */
    }

    const handler = (payload: any) => {
      if (payload?.state === "Started") renderSelf();
      else if (payload?.state === "Stopped") stopSelf();
    };
    zmClient.on("video-capturing-change", handler);
    return () => {
      zmClient.off("video-capturing-change", handler);
    };
  }, [zmClient, selfId]);

  // Defensive polling tick. peer-video-state-change only fires for video
  // transitions, so a participant who joins AFTER the host already had
  // video on never gets an event to trigger rendering. The effect above
  // does try at mount, but if the SDK's stream isn't fully subscribed yet
  // the call fails — this tick retries every 2s for any bVideoOn user
  // we haven't successfully rendered.
  useEffect(() => {
    const stream: any = zmClient?.getMediaStream?.();
    if (!stream) return;
    let stopped = false;

    const interval = window.setInterval(async () => {
      if (stopped) return;
      for (const p of participants) {
        if (!p.bVideoOn) continue;
        if (renderedRef.current.has(p.userId)) continue;
        const el = elementsRef.current.get(p.userId);
        if (!el) continue;
        try {
          const w = 640;
          const h = 360;
          if (el instanceof HTMLCanvasElement && el.width !== w) {
            el.width = w;
            el.height = h;
          }
          await stream.renderVideo(el, p.userId, w, h, 0, 0, 2);
          renderedRef.current.add(p.userId);
        } catch {
          /* will retry on the next tick */
        }
      }
    }, 2000);

    return () => {
      stopped = true;
      window.clearInterval(interval);
    };
  }, [participants, zmClient, selfId]);

  if (participants.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "rgba(255,255,255,0.6)",
        }}
      >
        Waiting for others to join…
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        position: "relative",
        overflow: "hidden",
        p: 1.5,
        ...(containerSxFor(layout, ordered.length) as any),
      }}
    >
      {ordered.map((p, idx) => {
        const tile = tileSxFor(layout, idx, p.userId === selfId);
        return (
          <Tile
            key={p.userId}
            participant={p}
            isSelf={p.userId === selfId}
            useVideoElement={shouldUseVideoElement(zmClient, p.userId, selfId)}
            refSetter={getRefSetter(p.userId)}
            box={tile.box}
            isSelfPip={tile.isSelfPip}
            isSpeaking={!!speakingIds?.has(p.userId)}
          />
        );
      })}
    </Box>
  );
}

interface TileProps {
  participant: any;
  isSelf: boolean;
  useVideoElement: boolean;
  refSetter: (el: HTMLCanvasElement | HTMLVideoElement | null) => void;
  box?: any;
  isSelfPip?: boolean;
  isSpeaking?: boolean;
}

function Tile({
  participant: p,
  isSelf,
  useVideoElement,
  refSetter,
  box,
  isSelfPip,
  isSpeaking,
}: Readonly<TileProps>) {
  const showMedia = p.bVideoOn;
  return (
    <Box
      sx={{
        position: "relative",
        bgcolor: "#1f2229",
        borderRadius: 1,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        outline: isSpeaking ? "3px solid" : "none",
        outlineColor: "success.light",
        outlineOffset: "-3px",
        transition: "outline-color 150ms ease-in-out",
        ...box,
        ...(isSelfPip
          ? {
              position: "absolute",
              right: 16,
              bottom: 16,
              width: "22%",
              maxWidth: 260,
              minWidth: 160,
              aspectRatio: "16 / 9",
              boxShadow: 4,
              border: "2px solid rgba(255,255,255,0.15)",
              zIndex: 2,
            }
          : null),
      }}
    >
      {/* Self may need <video> per SDK; everyone else uses <canvas>. */}
      {isSelf && useVideoElement ? (
        <video
          ref={refSetter as any}
          autoPlay
          playsInline
          muted
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: showMedia ? "block" : "none",
          }}
        />
      ) : (
        <canvas
          ref={refSetter as any}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: showMedia ? "block" : "none",
          }}
        />
      )}
      {!showMedia && (
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            bgcolor: "rgba(255,255,255,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 28,
            color: "rgba(255,255,255,0.85)",
          }}
        >
          {(p.displayName?.[0] ?? "?").toUpperCase()}
        </Box>
      )}
      <Box
        sx={{
          position: "absolute",
          bottom: 6,
          left: 8,
          fontSize: 12,
          color: "white",
          bgcolor: "rgba(0,0,0,0.45)",
          px: 1,
          py: 0.25,
          borderRadius: 0.5,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        {isSpeaking && <SpeakingIndicator label={p.displayName} />}
        {p.displayName}
        {isSelf ? " (you)" : ""}
        {p.muted && <span style={{ opacity: 0.7, fontSize: 11 }}>· muted</span>}
      </Box>
    </Box>
  );
}

function shouldUseVideoElement(
  zmClient: any,
  userId: number,
  selfId: number | undefined
): boolean {
  if (!zmClient || userId !== selfId) return false;
  try {
    return !!zmClient.getMediaStream?.()?.isRenderSelfViewWithVideoElement?.();
  } catch {
    return false;
  }
}

function orderForLayout(
  participants: any[],
  selfId: number | undefined,
  layout: StageLayout
): any[] {
  if (layout !== "two") return participants;
  const self = participants.find((p) => p.userId === selfId);
  const others = participants.filter((p) => p.userId !== selfId);
  if (!self || others.length === 0) return participants;
  return [others[0], self];
}

function containerSxFor(layout: StageLayout, n: number) {
  if (layout === "sidebar") {
    return {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
      overflowY: "auto",
    };
  }
  if (layout === "two") {
    // The PiP tile is positioned absolutely; the big tile fills the box.
    return { display: "block" };
  }
  const cols = gridCols(n);
  const rows = Math.ceil(n / cols);
  return {
    display: "grid",
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gridTemplateRows: `repeat(${rows}, 1fr)`,
    gap: "8px",
  };
}

function tileSxFor(
  layout: StageLayout,
  idx: number,
  isSelf: boolean
): TileBoxStyle {
  if (layout === "sidebar") {
    return {
      box: { flex: "0 0 auto", width: "100%", aspectRatio: "16 / 9" },
    };
  }
  if (layout === "two") {
    if (idx === 0) {
      return { box: { width: "100%", height: "100%" } };
    }
    return { box: {}, isSelfPip: isSelf };
  }
  return { box: { width: "100%", height: "100%", minHeight: 0 } };
}

function gridCols(n: number): number {
  if (n <= 1) return 1;
  if (n <= 4) return 2;
  if (n <= 9) return 3;
  if (n <= 16) return 4;
  return Math.ceil(Math.sqrt(n));
}
